import type { DesignSide, WearableConfig } from "../types";

/**
 * Generates a 1024x1024 template PNG that visually shows the printable
 * region for a given side. Used for the "Download front template" and
 * "Download back template" actions.
 *
 * The template has:
 *   - a transparent background
 *   - a bold outlined rectangle showing the printable region
 *   - a subtle grid inside the region
 *   - a side label
 */
export function generateTemplateCanvas(
  config: WearableConfig,
  side: DesignSide,
): HTMLCanvasElement {
  const size = config.textureSize;
  const box = side === "front" ? config.frontBox : config.backBox;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  // Transparent background is already the default.

  // Faint full-canvas border for reference.
  ctx.save();
  ctx.strokeStyle = "rgba(0,0,0,0.15)";
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, size - 2, size - 2);
  ctx.restore();

  // Printable area: semi-transparent fill + strong outline.
  ctx.save();
  ctx.fillStyle = "rgba(64, 128, 255, 0.08)";
  ctx.fillRect(box.x, box.y, box.width, box.height);

  ctx.strokeStyle = "rgba(30, 64, 175, 0.9)";
  ctx.lineWidth = 4;
  ctx.setLineDash([12, 8]);
  ctx.strokeRect(box.x, box.y, box.width, box.height);
  ctx.restore();

  // Grid inside the printable region (every 40 px).
  ctx.save();
  ctx.strokeStyle = "rgba(30, 64, 175, 0.15)";
  ctx.lineWidth = 1;
  const step = 40;
  ctx.beginPath();
  for (let gx = box.x + step; gx < box.x + box.width; gx += step) {
    ctx.moveTo(gx, box.y);
    ctx.lineTo(gx, box.y + box.height);
  }
  for (let gy = box.y + step; gy < box.y + box.height; gy += step) {
    ctx.moveTo(box.x, gy);
    ctx.lineTo(box.x + box.width, gy);
  }
  ctx.stroke();
  ctx.restore();

  // Labels.
  ctx.save();
  ctx.fillStyle = "rgba(30, 64, 175, 0.9)";
  ctx.font = "bold 28px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(
    `${side.toUpperCase()} — ${config.label} (${config.gender})`,
    box.x,
    box.y - 40,
  );

  ctx.font = "14px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillStyle = "rgba(30, 64, 175, 0.7)";
  ctx.fillText(
    `Region: ${box.x},${box.y} ${box.width}x${box.height}  —  Texture: ${size}x${size}`,
    box.x,
    box.y + box.height + 8,
  );
  ctx.restore();

  return canvas;
}
